import React, { useState } from "react";
import { ethers } from "ethers";

import './App.scss';

const NETWORKS = {
  1: {
    blockExplorerDomain: 'https://etherscan.io/',
    name: 'Ethereum',
    color: '#666666',
  },
  10: {
    blockExplorerDomain: 'https://optimistic.etherscan.io/',
    name: 'Optimism',
    color: '#e06666',
  },
  42161: {
    blockExplorerDomain: 'https://explorer.arbitrum.io/',
    name: 'Arbitrum',
    color: '#1B4ADD',
  },
  5: {
    blockExplorerDomain: 'https://goerli.etherscan.io/',
    name: 'Goerli',
    color: 'paleturquoise',
  },
  11155111: {
    blockExplorerDomain: 'https://sepolia.etherscan.io/',
    name: 'Sepolia',
    color: '#cfb5f0',
  },
  84531: {
    blockExplorerDomain: 'https://goerli.basescan.org/',
    name: 'Base Goerli',
    color: '#588AF8',
  }
}

function isValidBigNumber(input) {
  try {
    const amount = ethers.utils.parseEther(input)
    return ethers.BigNumber.from(amount).gte(0);
  } catch (e) {
    return false;
  }
}

function App() {
  const [recipient, setRecipientAddress] = useState('');
  const [message, setMessage] = useState('');
  const [amount, setAmount] = useState('');
  const [connected, setConnected] = useState(false);
  const [chainId, setChainId] = useState('');
  const [address, setAddress] = useState('');
  const [errorMessage, setErrorMessage] = useState("");

  const handleClose = () => {
    setErrorMessage(null);
  };

  const handleRecipientAddressChange = (event) => {
    setRecipientAddress(event.target.value);
  };

  const handleMessageChange = (event) => {
    setMessage(event.target.value);
  };

  const handleAmountChange = (event) => {
    setAmount(event.target.value);
  };

  const handleConnectClick = async () => {
    if (window.ethereum) {
      try {
        await window.ethereum.enable();
        const provider = new ethers.providers.Web3Provider(window.ethereum);
        const network = await provider.getNetwork();
        setChainId(network.chainId);

        const signer = provider.getSigner();
        const address = await signer.getAddress();
        setAddress(address);

        window.ethereum.on("accountsChanged", async () => {
          const signer = provider.getSigner();
          const address = await signer.getAddress();
          setAddress(address);
        })

        window.ethereum.on('chainChanged', async () => {
          const provider = new ethers.providers.Web3Provider(window.ethereum);
          const network = await provider.getNetwork();
          setChainId(network.chainId);
        })
        setConnected(true);
      } catch (error) {
        console.error(error);
      }
    } else {
      console.error('Web3 not found');
    }
  };

  const handleDisconnectClick = async () => {
    if (window.ethereum) {
      try {
        await window.ethereum.request({
          method: "eth_requestAccounts",
          params: [{eth_accounts: {}}]
      })
        setConnected(false);
      } catch (error) {
        console.error(error);
      }
    } else {
      console.error('Web3 not found');
    }
  };

  const handleSendClick = async () => {
    if (connected) {
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const network = await provider.getNetwork();
      const chainId = network.chainId;
      const signer = provider.getSigner();

      let amountToSend = amount === "" ? "0" : amount;
      if (!isValidBigNumber(amountToSend)) {
        setErrorMessage("Please enter a valid amount of ETH.");
        return;
      }

      const balance = await provider.getBalance(signer.getAddress());
      if(balance.lt(ethers.utils.parseEther(amountToSend))) {
        setErrorMessage("The amount you want to send exceeds your balance.");
        return;
      }
      
      let recipientAddress = "";
      if (!recipient) {
        setErrorMessage("The recipient field is required.");
      } else if (ethers.utils.isAddress(recipient)) {
        recipientAddress = recipient;
      } else {
        if(chainId === 1) {
          recipientAddress = await provider.resolveName(recipient);
        } else {
          setErrorMessage('ENS names are only supported on Ethereum mainnet. If you meant to use mainnet, switch to mainnet in your wallet. Otherwise enter the recipient address directly (e.g. 0xaf4...).');
        }
      }

      const transaction = {
        to: recipientAddress,
        value: ethers.utils.parseEther(amountToSend),
        data: ethers.utils.toUtf8Bytes(message),
      };
      try {
        const response = await signer.sendTransaction(transaction);
        const transactionHash = response.hash;
        const transactionStatusElement = document.getElementById('transaction-status');
        const etherscanUrl = `${NETWORKS[chainId].blockExplorerDomain}tx/${transactionHash}`;
        const transactionHashLink = document.createElement('a');
        transactionHashLink.href = etherscanUrl;
        transactionHashLink.target = '_blank';
        transactionHashLink.rel = 'noopener noreferrer';
        transactionHashLink.textContent = transactionHash;
        transactionStatusElement.innerHTML = '';
        transactionStatusElement.appendChild(transactionHashLink);
      } catch (error) {
        console.error(error);
      }
    } else {
      console.error('Not connected to Web3');
    }
  };

  const network = NETWORKS[chainId];
  let formStyle = {};
  if(network) {
    formStyle = {
      boxShadow: `10px 10px 0px 0px ${network.color}`
    }
  }

  return (
    <div className="App">
      <header>
        <h1>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
            <path fill="#ffffff" d="M12 22.45L2.39 16.14l1.22-2.11L12 18.27l8.39-4.24 1.22 2.11L12 22.45zM12 5.55L21.61 11.86l-1.22 2.11L12 9.73 3.61 14.97l-1.22-2.11L12 5.55z"/>
            <path fill="#6d9eeb" d="M12 2L0 8.69l3.26 5.67L12 7.55l8.74 4.81L24 8.69 12 2zm0 16.9L2.39 16.14l1.22-2.11L12 18.27l8.39-4.24 1.22 2.11L12 22.45zm0-14.18L21.61 11.86l-1.22 2.11L12 9.73 3.61 14.97l-1.22-2.11L12 5.55z"/>
          </svg>
          <span>ChainGram</span>
        </h1>
        <p>Welcome to ChainGram! This app allows you to send messages on the Ethereum blockchain by embedding text in the "data" field of a transaction.</p>
        <p>Once sent, you can view the message on Etherscan by finding your transaction there, clicking the "Click to show more" link, then "View Input As", and then "UTF-8".</p>
      </header>
      {!connected &&
        <button type="button" className="styled-button" onClick={handleConnectClick}>Connect</button>
      }
      {connected &&
        <>
          <form style={formStyle}>
            {!network &&
              <>
                <p>Unrecognized network. Please use your wallet to switch to one of these supported networks:<br/>
                Ethereum, Optimism, Arbitrum, Goerli, or Sepolia.</p>
              </>
            }
            {network &&
              <>
                <div className="row">
                  <div className="label">Network:</div>
                  <div className="value">{network.name}</div>
                </div>
                <div className="row">
                  <div className="label">Sender:</div>
                  <div className="value">{address}</div>
                </div>
                <label htmlFor="recipient-address">Recipient address</label>
                <input type="text" id="recipient-address" value={recipient} onChange={handleRecipientAddressChange} />
                <label htmlFor="message">Message</label>
                <input type="text" id="message" value={message} onChange={handleMessageChange} />
                <label htmlFor="amount">Amount of ETH to send (optional)</label>
                <input type="text" id="amount" value={amount} onChange={handleAmountChange} />
                <button type="button" className="styled-button" onClick={handleSendClick}>Send</button>
                <button type="button" onClick={handleDisconnectClick} className="disconnect-link">Disconnect</button>
              </>
            }
          </form>
          <div id="transaction-status"></div>
        </>
      }
      { errorMessage &&
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-content">
              <div className="close-button-container">
                <span className="close" onClick={handleClose}>&times;</span>
              </div>
              <p>{errorMessage}</p>
            </div>
          </div>
        </div>
      }
      <footer>
        <p>I'm ChatGPT, an AI language model trained by OpenAI, and I wrote this app using React and ethers.js.</p>
        <p>I also wrote some of the text and designed the logo.</p>
        <p>I had technical assistance from <a href="https://twitter.com/MikeSylphDapps" target="_blank" rel="noreferrer">mike.sylphdapps.eth</a>.</p>
        <p>He posts all his projects on <a href="https://sylphdapps.com" target="_blank" rel="noreferrer">Sylph Dapps</a>.</p>
        <p>Enjoy!</p>
      </footer>
    </div>
  );
}

export default App;